/**
 * ULIDs and slugs.
 *
 * Every table uses a ULID text primary key: lexicographically sortable by creation time,
 * which means `ORDER BY id` is a free chronological order and D1's b-tree stays append-ish
 * instead of scattering like it would with UUIDv4.
 *
 * Implemented here rather than pulled from npm so the Worker, the CLI and the tests all
 * agree on the encoding without a dependency that has to work in three runtimes.
 */
/** Crockford base32 — no I, L, O or U, so ids survive being read aloud or retyped. */
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const TIME_LEN = 10;
const RANDOM_LEN = 16;
/** Generate a ULID. `now` is injectable so tests can produce a deterministic ordering. */
export function ulid(now = Date.now()) {
    return encodeTime(now) + encodeRandom();
}
function encodeTime(now) {
    if (!Number.isFinite(now) || now < 0 || now > 0xffffffffffff) {
        throw new RangeError(`ULID timestamp out of range: ${now}`);
    }
    let time = Math.floor(now);
    let out = '';
    for (let i = 0; i < TIME_LEN; i++) {
        out = ENCODING[time % 32] + out;
        time = Math.floor(time / 32);
    }
    return out;
}
function encodeRandom() {
    const bytes = new Uint8Array(RANDOM_LEN);
    crypto.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < RANDOM_LEN; i++) {
        // Each byte contributes one base32 character; the top 3 bits are discarded, which
        // still leaves 80 bits of entropy across the 16 characters.
        out += ENCODING[bytes[i] % 32];
    }
    return out;
}
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
export function isUlid(value) {
    return ULID_RE.test(value);
}
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** URL-safe identifier used for team, app and channel slugs. */
export function isValidSlug(value) {
    return value.length >= 2 && value.length <= 64 && SLUG_RE.test(value);
}
const USERNAME_RE = /^[a-z0-9][a-z0-9_-]{1,30}[a-z0-9]$/;
/**
 * The public handle. This is the *only* name a person who shares no team with you ever sees, so
 * it is deliberately not a slug: `_` is allowed because handles read as handles, and the 3-to-32
 * range is the one people expect from every other site that asks for one.
 *
 * Must start and end alphanumeric, which keeps `@-foo` and `foo_` out and leaves room for a
 * trailing disambiguator without it looking like part of the name.
 */
export function isValidUsername(value) {
    return USERNAME_RE.test(value);
}
/** Best-effort slugification, used to suggest a slug from a display name. */
export function slugify(input) {
    return input
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64)
        .replace(/-+$/g, '');
}
//# sourceMappingURL=ids.js.map